import 'client-only';

import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { PaymentsState } from '@/atoms/payments/paymentsState';
import { useCreditCardForm } from '@/components/payments/hooks/useCreditCardForm';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { AnalyticsEvent, trackEvent } from '@/utils/analytics';

/**
 * Mounts a stripe credit card element to the dom via a constant input element ID. The tree
 * should contain one element with the input id, and it's parent should have the container id.
 *
 * Wrap the page in the CreditCardClientProvider to ensure the Stripe client context is loaded.
 */
export const useStripeElement = () => {
  const stripeClient = useStripeClient();
  const [stripeElement, setStripeElement] = useAtom(
    PaymentsState.StripeElementAtom,
  );
  const { ccFormMeta, setCcFormMeta, resetCcForm } = useCreditCardForm();

  const stripeCcInputId = 'stripe-cc-input';
  const stripeCcInputContainerId = 'stripe-cc-input-container';

  const isInputLoaded = (inputId: string): boolean =>
    !!document.querySelector(`#${inputId}`)?.hasChildNodes();

  useEffect(() => {
    const elements = stripeClient?.elements();

    if (elements && !isInputLoaded(stripeCcInputId)) {
      const card = elements.create('card', {
        hidePostalCode: true,
      });

      card.mount(`#${stripeCcInputId}`);

      setStripeElement((prev) => ({ ...prev, card }));

      card.addEventListener('change', (event) => {
        setCcFormMeta({
          errorMsg: (event && event.error && event.error.message) || undefined,
          formChanged: !!event && !event.empty,
          formComplete: !!event && event.complete,
        });
      });

      card.addEventListener('blur', () => {
        trackEvent(AnalyticsEvent.FIELD_ENTERED, { field: 'card' });
      });
    }

    return () => {
      if (stripeElement.card) {
        stripeElement.card.unmount();
        setStripeElement((previous) => ({ ...previous, card: undefined }));
        resetCcForm();
      }
    };
  }, [
    resetCcForm,
    setCcFormMeta,
    setStripeElement,
    stripeClient,
    stripeElement.card,
  ]);

  return {
    inputErrorMessage: ccFormMeta.errorMsg,
    showCcInputLoader: !stripeElement.card,
    ccInputId: stripeCcInputId,
    ccInputContainerId: stripeCcInputContainerId,
  };
};
